import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  fetchGetImageApiV1DatasetDatasetIdQueryImageQueryImageIdGet,
  fetchGetVertexApiV1ExploreDatasetIdMediaMediaIdVertexGet,
} from "vl-api/vlComponents";
import { QueryImage, SimilarityClusterView } from "vl-api/vlSchemas";
import { VqlQuery } from "types";

interface UseVisualSimilarityImagesProps {
  vqlQueries: VqlQuery[];
  datasetId: string;
}

// Extended types to ensure both have image_uri for display
type NormalizedSimilarityClusterView = SimilarityClusterView & {
  image_uri?: string;
};
type NormalizedQueryImage = QueryImage;

interface UseVisualSimilarityImagesReturn {
  imageDataMap: Record<
    string,
    NormalizedSimilarityClusterView | NormalizedQueryImage
  >;
  isLoading: boolean;
}

interface MediaInfo {
  mediaId: string;
  operationType: "media" | "upload";
}

/**
 * Hook to fetch image data for visual similarity queries using different endpoints:
 *
 * VQL Upgrade Enabled:
 * - Upload images: /api/v1/dataset/{datasetId}/query_image/{queryImageId}
 * - Media images: /api/v1/dataset_image/{datasetId}/{imageId}
 *   (Both return single image data, fixing the "wrong image selected" issue)
 *
 * VQL Upgrade Disabled (Original behavior):
 * - Upload images: /api/v1/dataset/{datasetId}/query_image/{queryImageId}
 * - Media images: /api/v1/explore/{datasetId}/media/{mediaId}/vertex
 *   (Media returns cluster data with previews[0], may select wrong image)
 */
export const useVisualSimilarityImages = ({
  vqlQueries,
  datasetId,
}: UseVisualSimilarityImagesProps): UseVisualSimilarityImagesReturn => {
  // Extract visual similarity media info from VQL queries
  const visualSimilarityMediaInfo = useMemo(() => {
    const mediaInfos = vqlQueries
      .filter(
        (vqlQuery) =>
          "similarity" in vqlQuery &&
          vqlQuery.similarity &&
          (vqlQuery.similarity.op === "media" ||
            vqlQuery.similarity.op === "upload"),
      )
      .map((vqlQuery) => {
        if ("similarity" in vqlQuery && vqlQuery.similarity) {
          return {
            mediaId: vqlQuery.similarity.value,
            operationType: vqlQuery.similarity.op as "media" | "upload",
          };
        }
        return null;
      })
      .filter((info): info is MediaInfo => info !== null);

    // Remove duplicates based on mediaId
    return mediaInfos.filter(
      (info, index, arr) =>
        arr.findIndex((item) => item.mediaId === info.mediaId) === index,
    );
  }, [vqlQueries]);

  const imageQueries = useMemo(() => {
    return visualSimilarityMediaInfo.map((mediaInfo) => ({
      queryKey: [
        "visual-similarity",
        mediaInfo.operationType,
        datasetId,
        mediaInfo.mediaId,
      ],
      queryFn: () => {
        if (mediaInfo.operationType === "upload") {
          return fetchGetImageApiV1DatasetDatasetIdQueryImageQueryImageIdGet({
            pathParams: {
              datasetId: datasetId,
              queryImageId: mediaInfo.mediaId,
            },
          });
        } else {
          // Media operation: use different endpoint based on VQL upgrade flag
          // VQL upgrade OFF: use original vertex endpoint behavior
          return fetchGetVertexApiV1ExploreDatasetIdMediaMediaIdVertexGet({
            pathParams: {
              datasetId: datasetId,
              mediaId: mediaInfo.mediaId,
            },
          });
        }
      },
      enabled: !!mediaInfo.mediaId && !!datasetId,
    }));
  }, [visualSimilarityMediaInfo, datasetId]);

  const imageResults = useQueries({
    queries: imageQueries,
  });

  const imageDataMap = useMemo(() => {
    const map: Record<
      string,
      NormalizedSimilarityClusterView | NormalizedQueryImage
    > = {};
    visualSimilarityMediaInfo.forEach((mediaInfo, index) => {
      const result = imageResults[index];
      if (mediaInfo.mediaId && result?.data) {
        if (mediaInfo.operationType === "upload") {
          // Upload operations always return QueryImage data
          map[mediaInfo.mediaId] = result.data as NormalizedQueryImage;
        } else {
          // Media operations: different handling based on VQL upgrade flag
          const vertexData = result.data as SimilarityClusterView;
          const imageUri =
            vertexData.previews?.[0]?.image_uri ||
            vertexData.previews?.[0]?.media_uri;
          map[mediaInfo.mediaId] = {
            ...vertexData,
            image_uri: imageUri,
          };
        }
      }
    });
    return map;
  }, [visualSimilarityMediaInfo, imageResults]);

  // Check if any queries are still loading
  const isLoading = imageResults.some((result) => result.isLoading);

  return {
    imageDataMap,
    isLoading,
  };
};
